import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { BookingSlabDirective } from '../../../core/classes/booking-slab/booking-slab';
import { PropertiesCityDetails } from '../../../core/interfaces/city';
import { IOfferProperty } from '../../../core/interfaces/offer-property';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { DateService } from '../../../core/services/date-service/date.service';
import { LocationService } from '../../../core/services/location/location.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { PropertyParamsService } from '../../../core/services/property-params/property-params.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { TrackingNewService } from '../../../core/services/tracking/tracking-new.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { ModalService } from '../../../core/services/modal/modal.service';
import { CityPickerComponent } from '../../../shared/components/city-picker/city-picker.component';
import { IListingData } from '../../../core/interfaces/listings';
import { environment } from '../../../../environments/environment';
import { HomepageTrackingElementID } from '../../../core/interfaces/tracking.interfaces';
import { BookingService } from '../../../core/services/booking/booking.service';
import { UnleashService } from '@shared/features/unleash/service/unleash.service';
import { HttpParams } from '@angular/common/http';
import { UnleashFeatureName } from '../../../core/interfaces/unleash.interfaces';

@Component({
  selector: 'app-search-widget',
  templateUrl: './search-widget.component.html',
  styleUrls: ['./search-widget.component.scss'],
  standalone: false
})
export class SearchWidgetComponent extends BookingSlabDirective implements OnInit, AfterViewInit, OnDestroy {
  showCityPicker = false;
  cityId: number;
  propertyId: number;
  isSticky = false;
  alertElement: HTMLElement;
  calendarOpenBySystem: boolean;
  arrivalDate: string;
  departureDate: string;
  tabIndex = 1;
  submitForm = false;
  bookingRefInfoSubmitted = false;
  bookingRefForm: UntypedFormGroup;
  isFetching = false;
  fetchingError = false;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  get searchWidgetShadowRoot(): Element['shadowRoot'] {
    return this._elemRef.nativeElement.shadowRoot;
  }

  get isElements(): boolean {
    return !!(window as any).__isAngularElement__;
  }

  @ViewChild('searchWidget', { static: true }) searchWidgetElement: ElementRef;
  @ViewChild(CityPickerComponent) cityPickerComponent: CityPickerComponent;

  @Input() title: string;
  @Input() topmargin: number;
  @Input() homeHeroSlider = false;
  @Input() elementLang: string;

  @HostListener('window:scroll', [])
  onScroll() {
    if (!this.searchWidgetElement) {
      return;
    }

    const bodyTop = window.scrollY;
    const { height: alertHeight = 0 } = this.alertElement?.getBoundingClientRect() ?? { height: undefined };
    const bookingWidgetMarginTop = parseInt(
      window.getComputedStyle(this.searchWidgetElement.nativeElement).marginTop,
      10
    );
    this.isSticky = Math.abs(bodyTop) > alertHeight + bookingWidgetMarginTop && !this.isMobile;
  }

  constructor(
    private readonly viewportService: ViewportService,
    private readonly _translateService: TranslateService,
    private readonly _cookieAppService: CookieAppService,
    private readonly _locationService: LocationService,
    private readonly _resizeService: ResizeService,
    private readonly _messageService: MessageService,
    private readonly _trackingNewService: TrackingNewService,
    private readonly _propertyParamsService: PropertyParamsService,
    private readonly _modalService: ModalService,
    protected _store: Store,
    dateService: DateService,
    @Inject(PLATFORM_ID) public platformId: Object,
    private readonly _elemRef: ElementRef,
    private readonly _router: Router,
    private readonly _fb: UntypedFormBuilder,
    private readonly bookingService: BookingService,
    private readonly unleashService: UnleashService<UnleashFeatureName>
  ) {
    super(_translateService, _cookieAppService, _locationService, platformId, dateService);
    this.bookingRefForm = this.initForm();
  }

  ngOnInit(): void {
    this.processDatesLabels();
  }

  ngAfterViewInit(): void {
    this.alertElement = document.querySelector('.alert');
  }

  ngOnDestroy(): void {
    this._destruction$.next();
    this._destruction$.complete();
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  get isTablet() {
    return this.viewportService.isTabletDevice();
  }

  get destinationDetails() {
    return {
      city_name: this.city?.city.city.en,
      city_id: this.cityId,
      country_code: this.city?.city.code,
      rooms: this.rooms
    };
  }

  get reservationDetails() {
    return {
      adults_number: this.adults,
      children_number: this.childrenAges?.length,
      arrival_date: this.arrivalDate,
      departure_date: this.departureDate
    };
  }

  get isNewDetailsEndpointEnabled() {
    return this.unleashService.isEnabled('guest-success-get-reservation-by-token');
  }

  get bookingReferenceData() {
    const reservationId = this.bookingRefForm.value.reservationId.trim();
    const lastName = this.bookingRefForm.value.lastName.trim();

    return { reservationId, lastName };
  }

  trackClickEvent(elementId: HomepageTrackingElementID) {
    this._trackingNewService.trackClickEvent({
      page_name: 'homepage',
      element_id: elementId,
      destination_details: this.destinationDetails,
      reservation_details: this.reservationDetails
    });
  }

  onCitySelected(city: PropertiesCityDetails) {
    this.city = { city: city, index: city.id };
    this.cityId = city.id;
    this.propertyId = city.property_count === 1 ? this.getUniquePropertyID(this.cityId) : null;

    if (this.isMobile || !!this.selectedDates) {
      return;
    }

    setTimeout(() => {
      this.showHideCalendar(true);
      this.trackCalendarOpenEvent();
      // this.selectedCity.emit(city);
    });
  }

  /**
   * @description
   * Shows and hides the city picker
   */
  showHideCityPicker(isCityPickerOpen: boolean): void {
    this.showCityPicker = isCityPickerOpen;
  }

  /**
   * @description
   * Shows/Hides the calendar
   */
  showHideCalendar(isCalendarOpen: boolean): void {
    if (!this.selectedDates && !isCalendarOpen) {
      // calendar closed without dates, stop tracking
      this.calendarOpenBySystem = false;
    }

    super.showHideCalendar(isCalendarOpen);
    this.sendMessages();
  }

  /**
   * @description
   * Send message to header component
   */
  sendMessages() {
    this._modalService.handleIosScrolling(this.showCalendar);

    this._messageService.updateOverlayStatus(
      this.showCalendar,
      this.showCalendar,
      this.viewportService.isTabletDevice()
    );
  }

  processBookedDates(ev: Date[]): void {
    super.processBookedDates(ev);
    this.processDatesLabels();

    if (this.calendarOpenBySystem) {
      this.trackDateSelectedEvent();
      this.calendarOpenBySystem = false;
    }
    this.sendMessages();
  }

  /**
   * @description
   * Updates the dates label and keeps all the date formats in sync
   */
  processDatesLabels(): void {
    const dateParts = this.selectedDatesLabel.split('-');
    this.arrivalDate = dateParts.length === 2 ? dateParts[0] : 'From';
    this.departureDate = dateParts.length === 2 ? dateParts[1] : 'To';
  }

  /**
   * @description
   * Makes a booking request
   */
  submitSearchRequest(ev): void {
    if (this.unselectedAge && this.guestDropdownOpened) {
      return;
    }

    if (!this.city && !this.cityId) {
      setTimeout(() => {
        this.cityPickerComponent.showHideCityPicker();
      });

      return;
    }

    this._trackingNewService.trackClickEvent({
      page_name: 'homepage',
      element_id: 'homepage_search_widget',
      destination_details: this.destinationDetails,
      reservation_details: this.reservationDetails
    });

    const listingData: IListingData = {
      selectedDates: this.selectedDates,
      rooms: this.rooms,
      guests: this.adults,
      childrenAges: this.childrenAges.toString(),
      cityId: this.cityId,
      useLocationChange: this.isElements
    };

    if (this.shouldRedirectToPDP()) {
      this._propertyParamsService.redirectToPropertiesListing({
        ...listingData,
        route: `/suites`,
        propertyId: this.propertyId
      });
    } else {
      this._propertyParamsService.redirectToPropertiesListing({
        ...listingData,
        route: `/listings/properties`
      });
    }
  }

  trackDateSelectedEvent() {
    this._trackingNewService.trackClickEvent({
      page_name: 'homepage',
      element_id: 'homepage_search_widget_calendar_date_select',
      destination_details: this.destinationDetails,
      reservation_details: this.reservationDetails
    });
  }

  trackCalendarOpenEvent() {
    this._trackingNewService.trackClickEvent({
      page_name: 'homepage',
      element_id: 'homepage_search_widget_calendar_auto_toggle',
      destination_details: this.destinationDetails,
      reservation_details: this.reservationDetails
    });
    this.calendarOpenBySystem = true;
  }

  /**
   * @description
   * Check whether the user should be redirected to PDP when selecting a city having only one property or not
   */
  shouldRedirectToPDP() {
    return this.propertyId;
  }

  /**
   * @description
   * Fetch all properties from the store
   */
  getAllProperties(): IOfferProperty[] {
    return this._store.selectSnapshot((state) => state.allProperties?.allProperties);
  }

  /**
   * @description
   * Get the property from a city listing only one property
   */
  getUniquePropertyID(cityId): number | null {
    return Object.values(this.getAllProperties() || {})?.find((property) => property.city_id === cityId)?.id || null;
  }

  changeTab(index: number) {
    if (index === 2) {
      this.bookingRefForm.clearValidators();
      this.bookingRefForm.reset();
      this.bookingRefInfoSubmitted = false;
    }
    this.trackClickTab(index);
    this.tabIndex = index;
  }

  initForm() {
    return this._fb.group({
      reservationId: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(11)]],
      lastName: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  getGuestHubInfo() {
    this.bookingRefInfoSubmitted = true;
    if (!this.bookingRefForm.valid) return;
    this.isFetching = true;
    this.fetchingError = false;

    this._trackingNewService.trackClickEvent({
      page_name: 'homepage',
      element_id: 'homepage_search_widget_guest_hub_submit_button',
      destination_details: this.destinationDetails,
      reservation_details: {
        reservation_id: this.bookingReferenceData.reservationId.trim(),
        ...this.reservationDetails
      }
    });

    if (this.isNewDetailsEndpointEnabled) {
      return this.processReservationByToken();
    }

    const urlParams = new URLSearchParams(Object.entries(this.bookingReferenceData));
    this._locationService.redirectToExternal(`${environment.limeStayBaseUrl}/guest-hub?${urlParams.toString()}`);
  }

  processReservationByToken() {
    const { reservationId, lastName } = this.bookingReferenceData;

    this.bookingService.getReservationToken({ apaleoReservationId: reservationId, lastName }).subscribe({
      next: ({ payload: { reservationToken } }) => {
        const urlParams = new HttpParams({ fromObject: { reservationId, reservationToken } });
        this.locationService.redirectToExternal(`${environment.limeStayBaseUrl}/guest-hub?${urlParams.toString()}`);
      },
      error: (error) => {
        const isUnauthorizedStatus = error.status === 401;
        const message = isUnauthorizedStatus
          ? 'search-widget.unauthorized-credentials'
          : 'booking-reference.booking-reservation-error';
        this.bookingRefForm.setErrors({
          authentication: {
            message
          }
        });
      }
    });
    this.isFetching = false;
  }

  trackClickTab(index: number) {
    if (index === 2) {
      this._trackingNewService.trackClickEvent({
        page_name: 'homepage',
        element_id: 'homepage_search_widget_guest_hub_tab',
        destination_details: this.destinationDetails,
        reservation_details: this.reservationDetails
      });
    }
  }
}
